import React, { FC } from 'react';
import { faArrowAltCircleRight } from '@fortawesome/free-solid-svg-icons/faArrowAltCircleRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AnonymousUserDisplay from '@buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import AnonEmployerAssociation from '@buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import IdeaUpdateImpact from '@buyside/components/presentation/Feed/IdeaUpdate/IdeaUpdateImpact/ideaUpdateImpact';
import Link from '@buyside/components/presentation/Link/link';

import { employerUrl, ideaUpdateUrl, userUrl } from '@buyside/util/urlHelper';
import { truncateText } from '@buyside/util/textHelpers';

import { HomePageIdeaUpdatesQuery } from '@interfaces/graphql-typings';

import styles from '@buyside/components/presentation/HomePage/homePageStyles.scss';

const HomePageIdeaUpdateCard: FC<HomePageIdeaUpdatesQuery['homeIdeaUpdates'][number] & {
  canViewUserDetails: boolean;
}> = ({ canViewUserDetails, changesHash, closed, id, idea, impact, postedAt, text, user }) => {
  return (
    <div className={styles.item}>
      <div className={styles.innerItem}>
        <div className={styles.heading}>
          <div>
            <Link
              className={styles.itemTitle}
              target="_blank"
              to={ideaUpdateUrl(idea?.id || '', id)}
            >
              {idea?.entityName}
            </Link>
            <span className={styles.bbgSymbol}>{idea?.assetSymbol}</span>
          </div>
          <div className={styles.postedAt}>{postedAt} ago</div>
        </div>
        <div className={styles.impact}>
          <IdeaUpdateImpact closed={closed} impact={impact} />
        </div>
        <Link
          className={styles.linkText}
          target="_blank"
          title={idea?.entityName}
          to={ideaUpdateUrl(idea?.id || '', id)}
        >
          {text && <p>{truncateText(text, 155)}</p>}
        </Link>
        {Boolean(JSON.parse(changesHash).length) && (
          <div className={styles.ideaUpdateChanges}>
            {JSON.parse(changesHash).map(
              (change: { value: string; removed: string; added: string }, idx: number) => {
                return (
                  <div key={idx}>
                    <strong>{change.value}:</strong>{' '}
                    <span className={styles.removed}>{change.removed}</span>{' '}
                    <FontAwesomeIcon className={styles.arrowRight} icon={faArrowAltCircleRight} />{' '}
                    <span className={styles.added}>{change.added}</span>
                  </div>
                );
              }
            )}
          </div>
        )}
        <div className={styles.authorInfo}>
          {canViewUserDetails ? (
            <>
              <Link target="_blank" to={userUrl(user?.id)} className={styles.authorName}>
                {user?.displayName}
              </Link>{' '}
              {user?.mostRecentEmployer?.employer?.id && (
                <>
                  <Link
                    className={styles.authorEmployer}
                    target="_blank"
                    to={employerUrl(user?.mostRecentEmployer?.employer?.id)}
                  >
                    {user?.mostRecentEmployer?.employer?.name}
                  </Link>
                </>
              )}
            </>
          ) : (
            <>
              <div className={styles.authorName}>
                <AnonymousUserDisplay anonUser={{ title: user?.mostRecentEmployer?.title }} />
              </div>
              <AnonEmployerAssociation
                aumRange={user?.mostRecentEmployer?.employer?.aumRange}
                fundType={user?.mostRecentEmployer?.employer?.fundType}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePageIdeaUpdateCard;
